import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import jwtDecode from "jwt-decode";

import Loader from "../../../components/loader/loader";
import Snackbar from "../../../components/snackbar/snackbar";

import {
  adminBulkEmailDetail,
  superadminBulkEmailDetail,
  superadminBulkEmailApprove,
} from "../admin.api";

export default function BulkEmailDetail(props) {
  const { uuid } = useParams();
  const [bulkEmail, setBulkEmail] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [recipientsTotalPages, setRecipientsTotalPages] = useState(1);
  const [activeTab, setActiveTab] = useState("user");

  const [isActive, setIsActive] = useState(true);
  const [snackMessage, setSnackMessage] = useState("");
  const [isSnackError, setIsSnackError] = useState(false);
  const [snackOpen, setSnackOpen] = useState(false);

  const adminType = Number(jwtDecode(props.state.payload).user_type);
  const baseApiUrl = process.env.REACT_APP_BASE_URL || "";
  const fileBaseUrl = baseApiUrl.endsWith("/api")
    ? baseApiUrl.slice(0, -4)
    : baseApiUrl;

  const fetchDetail = (page = 1) => {
    if (!uuid) {
      setIsActive(false);
      return;
    }
    setIsActive(true);

    const apiCall =
      adminType === 2
        ? superadminBulkEmailDetail(uuid, page)
        : adminBulkEmailDetail(uuid, page);

    apiCall
      .then((res) => {
        if (res?.success && res.data) {
          const bulk = res.data.bulk_email || null;
          const recipientPager = res.data.recipients || {};
          const recipientData = recipientPager.data || [];
          setBulkEmail(bulk);
          setRecipients(recipientData);
          setRecipientsPage(recipientPager.current_page || page);
          setRecipientsTotalPages(recipientPager.last_page || 1);
        } else {
          setBulkEmail(null);
          setRecipients([]);
          setSnackMessage("Failed to load bulk email details");
          setIsSnackError(true);
          setSnackOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error fetching bulk email detail:", error);
        setBulkEmail(null);
        setRecipients([]);
        setSnackMessage("Failed to load bulk email details");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  useEffect(() => {
    fetchDetail(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uuid]);

  const snackbarClose = (flag) => {
    setSnackMessage("");
    setIsSnackError(false);
    setSnackOpen(false);
    if (!flag && !bulkEmail) {
      props.history.push("/bulk-emails");
    }
  };

  const handleRecipientsPageChange = (page) => {
    if (page < 1 || page > recipientsTotalPages) return;
    fetchDetail(page);
  };

  const renderRecipientStatus = (status) => {
    const raw = status || "";
    const value = String(raw).toLowerCase();
    let label = raw || "--";
    let cls = "badge bg-secondary";

    if (["sent", "delivered", "success", "successful"].includes(value)) {
      label = "Sent";
      cls = "badge bg-success";
    } else if (["pending", "queued", "processing", "submitted"].includes(value)) {
      label = "Submitted";
      cls = "badge bg-warning text-dark";
    } else if (["failed", "bounced", "error"].includes(value)) {
      label = "Failed";
      cls = "badge bg-danger";
    } else if (["opened", "read"].includes(value)) {
      label = "Opened";
      cls = "badge bg-info text-dark";
    }

    return <span className={`${cls} rounded-0`}>{label}</span>;
  };

  const renderRecipientsPagination = () => {
    if (recipientsTotalPages <= 1) return null;
    const buttons = [];
    for (let i = 1; i <= recipientsTotalPages; i++) {
      buttons.push(
        <button
          key={i}
          className={`btn btn-sm ${
            recipientsPage === i ? "btn-primary" : "btn-outline-primary"
          }`}
          onClick={() => handleRecipientsPageChange(i)}
        >
          {i}
        </button>
      );
    }
    return <div className="btn-group">{buttons}</div>;
  };

  const attachmentUrl = (attachmentPath) => {
    if (!attachmentPath) return "#";
    return `${fileBaseUrl}/${attachmentPath}`;
  };

  const handleApprove = () => {
    if (!uuid || adminType !== 2) return;
    setIsActive(true);
    superadminBulkEmailApprove(uuid)
      .then((res) => {
        setSnackMessage(res?.message || "Bulk email approved successfully");
        setIsSnackError(false);
        setSnackOpen(true);
        fetchDetail(recipientsPage);
      })
      .catch((error) => {
        setSnackMessage(error?.message || "Failed to approve bulk email");
        setIsSnackError(true);
        setSnackOpen(true);
      })
      .finally(() => {
        setIsActive(false);
      });
  };

  return (
    <div>
      <Loader open={isActive} />
      <Snackbar
        message={snackMessage}
        error={isSnackError}
        open={snackOpen}
        close={snackbarClose}
      />

      <div className="view-container">
        <span className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0 pt-2">
            <b>Bulk Email Details</b>
          </h6>
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => props.history.push("/bulk-emails")}
          >
            Back
          </button>
        </span>
        <hr />

        {!bulkEmail && !isActive && (
          <div className="alert alert-warning">Details not available.</div>
        )}

        {bulkEmail && (
          <>
            <ul className="nav nav-tabs border-0" style={{ borderBottom: "none" }}>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "user" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("user")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "user" ? "3px solid #0d6efd" : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Message Details
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link fw-bold ${activeTab === "recipients" ? "text-primary" : "text-muted"}`}
                  type="button"
                  onClick={() => setActiveTab("recipients")}
                  style={{
                    border: "none",
                    borderBottom:
                      activeTab === "recipients"
                        ? "3px solid #0d6efd"
                        : "3px solid transparent",
                    borderRadius: 0,
                    backgroundColor: "transparent",
                  }}
                >
                  Recipient Details
                </button>
              </li>
            </ul>

            {activeTab === "user" && (
              <div className="pt-3">
                <div className="data">
                  <div className="row">
                    <div className="col-3">Subject</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.subject}</div>
                  </div>
                  <div className="row align-items-start">
                    <div className="col-3">Body</div>
                    <div className="col-1">:</div>
                    <div className="col-7">
                      <div
                        className="border rounded p-2"
                        dangerouslySetInnerHTML={{ __html: bulkEmail.body_html }}
                      />
                    </div>
                  </div>
                  {Array.isArray(bulkEmail.attachments) && bulkEmail.attachments.length > 0 && (
                    <div className="row align-items-start">
                      <div className="col-3">Attachments</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">
                        <div className="d-flex flex-column gap-1">
                          {bulkEmail.attachments.map((att, index) => (
                            <div key={`${att.path}-${index}`}>
                              <a href={attachmentUrl(att.path)} target="_blank" rel="noreferrer">
                                {att.name}
                              </a>
                              <small className="text-muted ms-2">
                                ({att.mime}, {att.size} bytes)
                              </small>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {bulkEmail.review_message && (
                    <div className="row align-items-start">
                      <div className="col-3">Review Message</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">{bulkEmail.review_message}</div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-3">User Category</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.user_category}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Status</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.status}</div>
                  </div>
                  {bulkEmail.metadata?.campaign && (
                    <div className="row">
                      <div className="col-3">Campaign</div>
                      <div className="col-1">:</div>
                      <div className="col-7 break-word">{bulkEmail.metadata.campaign}</div>
                    </div>
                  )}
                  <div className="row">
                    <div className="col-3">Priority</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.metadata?.priority ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Created At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.created_at}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Submitted At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.submitted_at ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Approved At</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.approved_at ?? "--"}</div>
                  </div>
                  <div className="row">
                    <div className="col-3">Total Recipients</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.total_recipients}</div>
                  </div>
                  {/* <div className="row">
                    <div className="col-3">Sent</div>
                    <div className="col-1">:</div>
                    <div className="col-7 break-word">{bulkEmail.sent_recipients}</div>
                  </div> */}
                  {bulkEmail.creator && (
                    <>
                      <div className="row">
                        <div className="col-3">Creator Name</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">{bulkEmail.creator.name}</div>
                      </div>
                      <div className="row">
                        <div className="col-3">Creator Email</div>
                        <div className="col-1">:</div>
                        <div className="col-7 break-word">{bulkEmail.creator.email}</div>
                      </div>
                    </>
                  )}
                </div>

                {adminType === 2 && bulkEmail?.status !== "sent" && (
                  <div className="d-flex justify-content-center mt-4">
                    <button
                      type="button"
                      className="btn btn-success px-4"
                      onClick={handleApprove}
                      disabled={isActive}
                    >
                      Approve
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === "recipients" && (
              <div className="pt-3">
                <div className="mb-3">
                  <h6>
                    <b>Recipients</b>
                  </h6>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered mb-2">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Recipient Type</th>
                          <th>Email</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {recipients.length === 0 && (
                          <tr>
                            <td colSpan="4" className="text-center">
                              No recipients found.
                            </td>
                          </tr>
                        )}
                        {recipients.map((rec) => (
                          <tr key={rec.id}>
                            <td>{rec.name}</td>
                            <td>{rec.recipient_type}</td>
                            <td>{rec.email}</td>
                            <td>{renderRecipientStatus(rec.send_status)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      Page {recipientsPage} of {recipientsTotalPages}
                    </div>
                    {renderRecipientsPagination()}
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
